// Minimal dependency-free .xlsx writer for report exports.
//
// exportXlsx("Balance Sheet.xlsx", [{ name: "Balance Sheet", rows: [...], widths: [40, 16] }])
//
// A row is an array of cells. A cell is a string, a number (shown as #,##0.00), null/undefined (blank), or
// { v, bold, int } — `bold` for headings and totals, `int` for counts / days that must not get decimals.

const encoder = new TextEncoder();

const escapeXml = (value) =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    // control characters are illegal in XML 1.0
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, "");

const columnName = (index) => {
  let name = "";
  for (let n = index + 1; n > 0; n = Math.floor((n - 1) / 26)) name = String.fromCharCode(65 + ((n - 1) % 26)) + name;
  return name;
};

// style ids, matching <cellXfs> below
const STYLE = { text: 0, textBold: 1, money: 2, moneyBold: 3, int: 4, intBold: 5 };

const cellXml = (cell, rowIndex, colIndex) => {
  const spec = cell !== null && typeof cell === "object" ? cell : { v: cell };
  const value = spec.v;
  if (value === null || value === undefined || value === "") return "";
  const ref = `${columnName(colIndex)}${rowIndex + 1}`;
  if (typeof value === "number" && Number.isFinite(value)) {
    // `plain` keeps Excel's General format — for quantities (up to 3 decimals) and counts
    const style = spec.plain
      ? spec.bold ? STYLE.textBold : STYLE.text
      : spec.int ? (spec.bold ? STYLE.intBold : STYLE.int) : spec.bold ? STYLE.moneyBold : STYLE.money;
    return `<c r="${ref}" s="${style}"><v>${value}</v></c>`;
  }
  return `<c r="${ref}" s="${spec.bold ? STYLE.textBold : STYLE.text}" t="inlineStr"><is><t xml:space="preserve">${escapeXml(value)}</t></is></c>`;
};

const sheetXml = ({ rows, widths }) => {
  const columnCount = rows.reduce((max, row) => Math.max(max, row.length), 0);
  const cols = Array.from({ length: columnCount }, (_, i) => {
    const width =
      widths?.[i] ??
      Math.min(60, Math.max(10, ...rows.map((row) => String((row[i] && typeof row[i] === "object" ? row[i].v : row[i]) ?? "").length + 2)));
    return `<col min="${i + 1}" max="${i + 1}" width="${width}" customWidth="1"/>`;
  }).join("");
  const body = rows
    .map((row, r) => `<row r="${r + 1}">${row.map((cell, c) => cellXml(cell, r, c)).join("")}</row>`)
    .join("");
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">${
    cols ? `<cols>${cols}</cols>` : ""
  }<sheetData>${body}</sheetData></worksheet>`;
};

const STYLES_XML = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><numFmts count="1"><numFmt numFmtId="164" formatCode="#,##0.00"/></numFmts><fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts><fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills><borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders><cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs><cellXfs count="6"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/><xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/><xf numFmtId="164" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/><xf numFmtId="164" fontId="1" fillId="0" borderId="0" xfId="0" applyNumberFormat="1" applyFont="1"/><xf numFmtId="1" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/><xf numFmtId="1" fontId="1" fillId="0" borderId="0" xfId="0" applyNumberFormat="1" applyFont="1"/></cellXfs><cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles></styleSheet>`;

const safeSheetName = (name, index, used) => {
  const base = String(name || `Sheet${index + 1}`).replace(/[[\]:*?/\\]/g, " ").trim().slice(0, 31) || `Sheet${index + 1}`;
  let candidate = base;
  for (let n = 2; used.has(candidate.toLowerCase()); n++) candidate = `${base.slice(0, 28)} ${n}`;
  used.add(candidate.toLowerCase());
  return candidate;
};

// ---- zip (stored, no compression) ----------------------------------------------------------------------------------

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    table[n] = c >>> 0;
  }
  return table;
})();

const crc32 = (bytes) => {
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) crc = CRC_TABLE[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
  return (crc ^ 0xffffffff) >>> 0;
};

const zip = (files) => {
  const parts = [];
  const central = [];
  let offset = 0;
  files.forEach(({ path, content }) => {
    const name = encoder.encode(path);
    const data = encoder.encode(content);
    const crc = crc32(data);

    const local = new DataView(new ArrayBuffer(30));
    local.setUint32(0, 0x04034b50, true);
    local.setUint16(4, 20, true); // version needed
    local.setUint16(6, 0x0800, true); // UTF-8 names
    local.setUint16(8, 0, true); // stored
    local.setUint16(10, 0, true);
    local.setUint16(12, 0x21, true); // 1980-01-01
    local.setUint32(14, crc, true);
    local.setUint32(18, data.length, true);
    local.setUint32(22, data.length, true);
    local.setUint16(26, name.length, true);
    local.setUint16(28, 0, true);
    parts.push(new Uint8Array(local.buffer), name, data);

    const entry = new DataView(new ArrayBuffer(46));
    entry.setUint32(0, 0x02014b50, true);
    entry.setUint16(4, 20, true);
    entry.setUint16(6, 20, true);
    entry.setUint16(8, 0x0800, true);
    entry.setUint16(10, 0, true);
    entry.setUint16(12, 0, true);
    entry.setUint16(14, 0x21, true);
    entry.setUint32(16, crc, true);
    entry.setUint32(20, data.length, true);
    entry.setUint32(24, data.length, true);
    entry.setUint16(28, name.length, true);
    entry.setUint32(42, offset, true);
    central.push(new Uint8Array(entry.buffer), name);

    offset += 30 + name.length + data.length;
  });
  const centralSize = central.reduce((sum, part) => sum + part.length, 0);
  const end = new DataView(new ArrayBuffer(22));
  end.setUint32(0, 0x06054b50, true);
  end.setUint16(8, files.length, true);
  end.setUint16(10, files.length, true);
  end.setUint32(12, centralSize, true);
  end.setUint32(16, offset, true);
  return new Blob([...parts, ...central, new Uint8Array(end.buffer)], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
};

export const buildXlsx = (sheets) => {
  const used = new Set();
  const named = sheets.map((sheet, i) => ({ ...sheet, name: safeSheetName(sheet.name, i, used) }));
  const files = [
    {
      path: "[Content_Types].xml",
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>${named
        .map((_, i) => `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`)
        .join("")}</Types>`,
    },
    {
      path: "_rels/.rels",
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>`,
    },
    {
      path: "xl/workbook.xml",
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets>${named
        .map((sheet, i) => `<sheet name="${escapeXml(sheet.name)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`)
        .join("")}</sheets></workbook>`,
    },
    {
      path: "xl/_rels/workbook.xml.rels",
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">${named
        .map((_, i) => `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`)
        .join("")}<Relationship Id="rId${named.length + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/></Relationships>`,
    },
    { path: "xl/styles.xml", content: STYLES_XML },
    ...named.map((sheet, i) => ({ path: `xl/worksheets/sheet${i + 1}.xml`, content: sheetXml(sheet) })),
  ];
  return zip(files);
};

export const exportXlsx = (filename, sheets) => {
  const url = URL.createObjectURL(buildXlsx(sheets));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename.endsWith(".xlsx") ? filename : `${filename}.xlsx`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// ---- cell helpers --------------------------------------------------------------------------------------------------

/** Backend BigDecimals arrive as JSON numbers; null stays blank. */
export const num = (value) => (value === null || value === undefined || value === "" ? null : Number(value));
export const bold = (value) => ({ v: value, bold: true });
export const int = (value, isBold = false) => ({ v: num(value), int: true, bold: isBold });
export const boldRow = (row) => row.map((cell) => (cell !== null && typeof cell === "object" ? { ...cell, bold: true } : { v: cell, bold: true }));

/**
 * Rows for a flat table: `columns` = [{ label, key | value(row), format: "money" | "qty" | "int" | undefined }].
 * Money and quantity columns are totalled (unless `total: false`); unformatted numbers (counts, days) are written as-is.
 */
export const tableRows = (columns, rows, { totals = false } = {}) => {
  const rawOf = (column, row) => (column.value ? column.value(row) : row[column.key]);
  const cellOf = (column, row) => {
    const raw = rawOf(column, row);
    if (column.format === "money" || column.format === "signedMoney") return num(raw);
    if (column.format === "qty") return { v: num(raw), plain: true };
    if (column.format === "int") return int(raw);
    if (typeof raw === "number") return { v: raw, plain: true };
    return raw ?? "";
  };
  const out = [columns.map((column) => bold(column.label)), ...rows.map((row) => columns.map((column) => cellOf(column, row)))];
  if (totals && rows.length) {
    out.push(
      columns.map((column, i) => {
        if (i === 0) return bold("Total");
        if ((column.format !== "money" && column.format !== "signedMoney" && column.format !== "qty") || column.total === false) return "";
        const sum = rows.reduce((acc, row) => acc + (Number(rawOf(column, row)) || 0), 0);
        return column.format === "qty" ? { v: Math.round(sum * 1000) / 1000, plain: true, bold: true } : bold(sum);
      })
    );
  }
  return out;
};
